import { useContext, useState } from 'react';
import { useUserStore } from '@/user/stores/userStore';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { Link, ListItemText, useTheme } from '@mui/material';
import { WorkspacesSettingsButton } from './components/WorkspacesSettingsButton/WorkspacesSettingsButton';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { CreateWorkspaceModal } from '@/base/layouts/components/Sidebar/WorkspacesMenu/components/CreateWorkspaceModal/CreateWorkspaceModal';
import { MenuToggleContext } from '@/base/layouts/contexts/MenuToggleContext';
import { sortById } from '@/common/utils/sortById';
import {
  CreateWorkspaceWrapper,
  FooterWrapper,
  HeaderLeftContent,
  HeaderWrapper,
  StyledListButton,
  TopWrapper,
  WorkspacesContainer
} from './WorkspacesMenu.styles';
import { GoBackLink } from '../UserMenu/UserMenu.styles';
import { WorkspaceListItem } from '@/common/components/WorkspaceListItem/WorkspaceListItem';
import { routeProvider } from '@/common/utils';

export const WorkspacesMenu = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { pathname } = useRouter();
  const { workspaces, setCurrentWorkspace, setWorkspaces } = useUserStore();
  const { goToMain, closeMenu } = useContext(MenuToggleContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const exactMatch = pathname.includes('workspaces');

  const onOpenCreateWorkspaceModal = () => {
    closeMenu();
    setIsCreateModalOpen(true);
  };

  const handleChangeWorkspace = (workspaceId: string): void => {
    const sortByCurrentId = sortById(workspaceId);
    const sortedWorkspaces = workspaces.sort(sortByCurrentId);

    window.localStorage.setItem('currentWorkspace', workspaceId);
    setCurrentWorkspace(workspaceId);
    setWorkspaces(sortedWorkspaces);
    goToMain();
  };

  return (
    <WorkspacesContainer>
      <TopWrapper>
        <HeaderWrapper>
          <HeaderLeftContent>
            <ListItemText primary={t('Your workspaces')} />
            <ListItemText primary={workspaces.length} />
          </HeaderLeftContent>

          <GoBackLink onClick={goToMain}>{t('Go back')}</GoBackLink>
        </HeaderWrapper>
        <StyledListButton>
          {workspaces.map((workspace) => (
            <WorkspaceListItem
              key={workspace.id}
              workspace={workspace}
              onClick={() => handleChangeWorkspace(workspace.id)}
              showCheck
              size="large"
            />
          ))}
        </StyledListButton>
      </TopWrapper>
      <FooterWrapper>
        <Link
          onClick={() => {
            setIsCreateModalOpen(true);
          }}
          underline="none"
        >
          <CreateWorkspaceWrapper>
            <AddIcon />
            <ListItemText
              primary={t('Create workspace')}
              primaryTypographyProps={{
                fontWeight: theme.typography.fontWeightBold
              }}
            />
          </CreateWorkspaceWrapper>
        </Link>

        <WorkspacesSettingsButton
          active={exactMatch}
          name={t('Settings')}
          link={routeProvider.workspaces}
          icon={SettingsIcon}
          handleCloseMenu={closeMenu}
        />
      </FooterWrapper>

      <CreateWorkspaceModal
        isModalOpen={isCreateModalOpen}
        setIsModalOpen={onOpenCreateWorkspaceModal}
      />
    </WorkspacesContainer>
  );
};
